import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import ErrorState from './ErrorState';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatGBP } from '../utils/money';

/**
 * <QuoteApprovalSettings /> — sign-off before a high-value quote is sent.
 * One configurable threshold: quotes at or above it can't be emailed until an
 * owner or admin (other than whoever raised it) approves them.
 */
const QuoteApprovalSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState('5000');
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const d = await apiFetch('/api/contractor/settings');
      const cfg = d?.data?.quoteApproval || {};
      const next = { enabled: !!cfg.enabled, threshold: Number.isFinite(+cfg.threshold) && cfg.threshold !== undefined ? String(+cfg.threshold) : '5000' };
      setEnabled(next.enabled);
      setThreshold(next.threshold);
      setSaved(next);
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const value = Number(threshold);
  const error = enabled && (threshold === '' || !Number.isFinite(value) || value <= 0)
    ? 'Enter an amount greater than £0'
    : (enabled && value > 10000000 ? 'Enter an amount under £10,000,000' : null);
  const dirty = !!saved && (enabled !== saved.enabled || String(threshold) !== saved.threshold);

  const save = async () => {
    if (error) return;
    setSaving(true);
    try {
      await apiFetch('/api/contractor/settings', {
        method: 'PUT',
        body: JSON.stringify({ data: { quoteApproval: { enabled, threshold: enabled ? value : (Number(saved?.threshold) || 0) } } }),
      });
      setSaved({ enabled, threshold: String(threshold) });
      toast.success(enabled ? `Quotes of ${formatGBP(value)} or more now need sign-off` : 'Quote sign-off turned off');
    } catch (err) { toast.error(apiErrorMessage(err, 'Could not save that setting')); }
    finally { setSaving(false); }
  };

  if (loading && !saved) return <div className="cw-card cw-card-pad" role="status"><span className="cw-meta">Loading quote sign-off…</span></div>;
  if (loadError && !saved) return <ErrorState compact thing="quote sign-off settings" error={loadError} onRetry={load} />;

  return (
    <section className="cw-card cw-card-pad" aria-labelledby="cw-qa-h">
      <h3 className="cw-h3" id="cw-qa-h" style={{ margin: '0 0 2px' }}>
        <i className="fa-solid fa-user-shield" aria-hidden="true" style={{ color: 'var(--cw-blue)', marginRight: 6 }} />
        Quote sign-off
      </h3>
      <p className="cw-meta" style={{ margin: '0 0 12px' }}>Ask for a second check before a high-value quote goes out.</p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer', marginBottom: 6 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ accentColor: 'var(--cw-blue)', width: 18, height: 18 }} />
        Quotes over a set value need approving before they can be emailed
      </label>
      {enabled && (
        <Field label="Approval threshold (£, net)" error={error}
          hint="A quote at or above this net value is held as Awaiting sign-off. Any company owner or admin can approve it, except whoever raised it.">
          <input style={{ maxWidth: 200 }} type="number" inputMode="decimal" min="1" step="1" value={threshold}
            onChange={(e) => setThreshold(e.target.value)} />
        </Field>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving || !!error || !dirty}>
          <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> {saving ? 'Saving…' : 'Save'}
        </button>
        {dirty && <span className="cw-meta" style={{ color: 'var(--cw-warning-fg)' }}>Unsaved changes</span>}
      </div>
    </section>
  );
};

export default QuoteApprovalSettings;
